
/**
 * Module dependencies.
 */

import { ProductFinish } from 'src/types/products';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import {
  finishes,
  finishesIconsSmall,
  finishesValues
} from 'src/core/collections-config/finishes';

import { getKeyFromObject } from 'src/core/utils/objects';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import React, { ReactElement, useMemo } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.finish;

/**
 * `Props` type.
 */

type Props = {
  filtersList?: ProductFinish[]
};

/**
 * `FinishFilter` component.
 */

const FinishFilter = ({ filtersList }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { filters } = useFiltersList();
  const hasFilter = keys(filters).includes(filterName);
  const selectedFilters = hasFilter ? split(filters[filterName], ',').map(finish => {
    const key = getKeyFromObject(finishesValues, finish);

    return translate(`collections:finishes.${key}`);
  }) : [];

  const finishesList = useMemo(() => {
    const orderedFinishes = sortBy(finishes, color => {
      return translate(`collections:finishes.${color}`);
    });

    if (isEmpty(filtersList)) {
      return orderedFinishes;
    }

    return filter(orderedFinishes, finish => {
      return includes(filtersList, finishesValues[finish]);
    });
  }, [filtersList, translate]);

  return (
    <FilterDisclosure
      name={filterName}
      selectedFilters={selectedFilters}
      title={translate('common:labels.finish')}
    >
      {finishesList.map((finish: string) => (
        <FilterItem
          key={finish}
          label={(
            <FilterItemLabel
              title={translate(`collections:finishes.${finish}`)}
              type={'icon'}
              value={finishesIconsSmall[finish]}
            />
          )}
          name={filterName}
          value={finishesValues[finish]}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `FinishFilter` component.
 */

export default FinishFilter;
